import { useMemo } from"react";
import {
 Eye,
 Flame,
 Trophy,
 CheckCircle2,
 XCircle,
 RotateCcw,
 BarChart3,
} from"lucide-react";
import { PCG } from"@/data/pcg";
import { CLASSES } from"@/data/classMeta";
import { QUIZZES } from"@/data/quizzes";
import { EXERCICES } from"@/data/exercices";
import ShareButtons from"@/components/ShareButtons";

function getRank(score) {
 // Score = % consultés + 2*quiz réussis + 5*exercices complets
 if (score < 5) return { label:"Novice", color:"#94A3B8", desc:"Vous démarrez votre apprentissage. Tout reste à explorer !" };
 if (score < 20) return { label:"Initié", color:"#5AA0FF", desc:"Les bases sont posées. Continuez à explorer les classes." };
 if (score < 50) return { label:"Apprenti", color:"#2D6BE0", desc:"Vous maîtrisez les comptes courants. Bel élan." };
 if (score < 100) return { label:"Confirmé", color:"#3B1E6E", desc:"Le PCG vous devient familier. Niveau pro junior." };
 if (score < 200) return { label:"Expert", color:"#15294F", desc:"Vous êtes à l'aise sur l'ensemble du référentiel." };
 return { label:"Maître", color:"#0F172A", desc:"Vous maîtrisez le PCG 2025 dans toute sa profondeur. Chapeau !" };
}

export default function Stats({ stats, onReset, onChangeView }) {
 const coverage = useMemo(() => {
 // % par classe
 const byClass = {};
 for (const cls of CLASSES) {
 const total = PCG.filter((a) => a.classe === cls.id).length;
 const viewed = stats.viewedAccounts.filter((num) => {
 const acc = PCG.find((a) => a.num === num);
 return acc && acc.classe === cls.id;
 }).length;
 byClass[cls.id] = {
 viewed,
 total,
 pct: total > 0 ? Math.round((viewed / total) * 100) : 0,
 };
 }
 return byClass;
 }, [stats.viewedAccounts]);

 const totalViewed = stats.viewedAccounts.length;
 const totalAccounts = PCG.length;
 const globalPct = Math.round((totalViewed / totalAccounts) * 100);

 const quizOk = stats.quizResults.filter((r) => r.ok).length;
 const quizKo = stats.quizResults.filter((r) => !r.ok).length;
 const quizTotal = quizOk + quizKo;
 const quizAccuracy = quizTotal > 0 ? Math.round((quizOk / quizTotal) * 100) : 0;

 const exerciceCount = stats.exerciceCompletions.length;
 const exerciceTotal = EXERCICES.length;

 const score = globalPct + quizOk * 2 + exerciceCount * 5;
 const rank = getRank(score);

 return (
 <div data-testid="stats-view" className="space-y-8">
 <div>
 <span className="inline-block px-3 py-1 text-xs font-bold rounded-full border border-keyto bg-white text-[#15294F] mb-3 shadow-keyto-sm">
 Tableau de bord
 </span>
 <h2 className="font-heading text-3xl md:text-5xl font-extrabold text-[#15294F] tracking-tight">
 Vos statistiques
 </h2>
 <p className="text-slate-600 mt-3 text-base max-w-2xl">
 Suivez votre progression sur les {PCG.length} comptes du PCG, vos performances aux quiz
 et vos exercices guidés.
 </p>
 </div>

 {/* Rank card */}
 <div className="bg-white border border-keyto rounded-2xl shadow-keyto p-6 md:p-8">
 <div className="flex flex-wrap items-center justify-between gap-6">
 <div>
 <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
 Votre rang actuel
 </div>
 <div className="flex items-center gap-4">
 <div
 className="w-16 h-16 rounded-2xl border-2 flex items-center justify-center shadow-keyto"
 style={{ borderColor: rank.color, backgroundColor: rank.color +"10" }}
 >
 <Trophy size={28} style={{ color: rank.color }} />
 </div>
 <div>
 <div
 className="font-heading text-3xl font-extrabold"
 style={{ color: rank.color }}
 >
 {rank.label}
 </div>
 <div className="text-sm text-slate-600 max-w-md">{rank.desc}</div>
 </div>
 </div>
 </div>
 <div className="text-right">
 <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">
 Score d'apprentissage
 </div>
 <div className="font-mono text-4xl font-extrabold text-[#15294F]">{score}</div>
 <div className="text-xs text-slate-500 mt-1">points cumulés</div>
 </div>
 </div>
 <div className="mt-6">
 <ShareButtons
 kind="default"
 scoreText={`Rang ${rank.label} (${score} pts) — ${totalViewed} comptes consultés, ${quizOk} quiz réussis.`}
 />
 </div>
 </div>

 {/* KPI grid */}
 <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
 <KPI
 icon={Eye}
 label="Comptes consultés"
 value={`${totalViewed} / ${totalAccounts}`}
 extra={`${globalPct} %`}
 color="#2D6BE0"
 />
 <KPI
 icon={CheckCircle2}
 label="Quiz réussis"
 value={`${quizOk} / ${quizTotal}`}
 extra={quizTotal ? `${quizAccuracy} % de réussite` :"Pas encore"}
 color="#2F7A5C"
 />
 <KPI
 icon={Flame}
 label="Série actuelle"
 value={`${stats.quizStreak}`}
 extra={`Record : ${stats.bestStreak}`}
 color="#B85724"
 />
 <KPI
 icon={Trophy}
 label="Exercices terminés"
 value={`${exerciceCount} / ${exerciceTotal}`}
 extra={exerciceCount === exerciceTotal && exerciceTotal > 0 ?"Tout est fait !" :""}
 color="#3B1E6E"
 />
 </div>

 {/* Coverage par classe */}
 <div className="bg-white border border-keyto rounded-2xl shadow-keyto p-6 md:p-8">
 <div className="flex items-center gap-3 mb-6">
 <BarChart3 size={20} className="text-[#15294F]" />
 <h3 className="font-heading text-xl font-extrabold text-[#15294F]">
 Couverture par classe
 </h3>
 </div>
 <div className="space-y-3">
 {CLASSES.map((cls) => {
 const c = coverage[cls.id];
 return (
 <div key={cls.id} className="flex items-center gap-4">
 <button
 data-testid={`stats-class-${cls.id}`}
 onClick={() => onChangeView && onChangeView("home")}
 className="font-mono font-extrabold text-xl w-8 shrink-0 text-left hover:underline"
 style={{ color: cls.color.text }}
 >
 {cls.id}
 </button>
 <div className="flex-1 min-w-0">
 <div className="flex items-baseline justify-between gap-2 mb-1">
 <span className="text-sm font-semibold text-slate-800 truncate">
 {cls.label}
 </span>
 <span className="text-xs font-mono font-bold text-slate-600 shrink-0">
 {c.viewed} / {c.total}
 </span>
 </div>
 <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
 <div
 className="h-full rounded-full transition-all duration-500"
 style={{
 width: `${c.pct}%`,
 backgroundColor: cls.color.primary,
 }}
 />
 </div>
 </div>
 <span className="text-sm font-mono font-bold text-slate-700 w-12 text-right shrink-0">
 {c.pct} %
 </span>
 </div>
 );
 })}
 </div>
 </div>

 {/* Historique quiz */}
 {stats.quizResults.length > 0 && (
 <div className="bg-white border border-keyto rounded-2xl shadow-keyto p-6 md:p-8">
 <h3 className="font-heading text-xl font-extrabold text-[#15294F] mb-4">
 Historique des quiz ({stats.quizResults.length} tentatives)
 </h3>
 <div className="flex flex-wrap gap-1.5">
 {stats.quizResults.slice(-50).map((r, i) => (
 <div
 key={i}
 title={`Quiz ${r.id} — ${new Date(r.ts).toLocaleString("fr-FR")}`}
 className={`w-8 h-8 rounded-md border flex items-center justify-center text-xs font-mono font-bold ${
 r.ok
 ?"bg-emerald-50 border-emerald-300 text-emerald-700"
 :"bg-rose-50 border-rose-300 text-rose-700"
 }`}
 >
 {r.ok ? <CheckCircle2 size={12} /> : <XCircle size={12} />}
 </div>
 ))}
 </div>
 </div>
 )}

 <div className="flex justify-end">
 <button
 data-testid="stats-reset"
 onClick={() => {
 if (window.confirm("Effacer toutes vos statistiques ? Cette action est irréversible.")) {
 onReset();
 }
 }}
 className="inline-flex items-center gap-2 text-xs font-semibold text-slate-500 hover:text-rose-600"
 >
 <RotateCcw size={12} /> Réinitialiser mes statistiques
 </button>
 </div>
 </div>
 );
}

function KPI({ icon: Icon, label, value, extra, color }) {
 return (
 <div className="bg-white border border-keyto rounded-xl shadow-keyto-sm p-5">
 <div className="flex items-center gap-3 mb-3">
 <div
 className="w-9 h-9 rounded-lg flex items-center justify-center"
 style={{ backgroundColor: color +"15", color }}
 >
 <Icon size={18} />
 </div>
 <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
 {label}
 </span>
 </div>
 <div className="font-mono text-2xl font-extrabold text-[#15294F]">{value}</div>
 {extra && <div className="text-xs text-slate-500 mt-1">{extra}</div>}
 </div>
 );
}
